import type { ReactElement } from 'react';

/**
 * Los iconos de navegación y de control.
 *
 * **Vocabulario distinto al de las marcas de hábito, a propósito** (paso 6): las
 * marcas son trazo de silueta; estos son relleno sólido. Si compartieran forma,
 * a 20 px la barra de navegación y la lista de hábitos se leerían igual, y el
 * usuario tendría que pensar dónde está mirando.
 *
 * Retícula de 24, como las marcas, para que el peso óptico case.
 */

type Props = { size?: number; className?: string };

function Svg({ size = 22, className, children }: Props & { children: ReactElement }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Hoy · una hoja de calendario. */
export const IconHoy = (p: Props): ReactElement => (
  <Svg {...p}>
    <g>
      <path d="M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5V8H4V6.5Z" />
      <path d="M4 10h16v7.5a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5V10Zm3.5 2.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Zm4.5 0a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5Z" />
    </g>
  </Svg>
);

/** Metas · una diana no, un objetivo con trayecto: barras que suben a un punto. */
/**
 * Metas · una bandera plantada en su mástil.
 *
 * Eran tres barras que subían, y Progreso son tres peldaños que suben: a 22 px
 * en una barra de navegación eran EL MISMO DIBUJO, y dos destinos contiguos que
 * se dibujan igual obligan a leer el rótulo para saber dónde estás. Que se
 * parecieran no era casualidad —los dos hablan de crecer—, y por eso el que se
 * mueve es Metas: la que tiene forma propia es la otra, porque el nivel ES una
 * escalera.
 *
 * Una bandera dice algo que las barras no decían: una meta no es una cantidad
 * que sube, es un sitio al que llegas y donde plantas algo. Y encaja con el
 * vocabulario del producto, que va entero de camino, umbral y cima.
 */
export const IconMetas = (p: Props): ReactElement => (
  <Svg {...p}>
    <path d="M5 2.75A.75.75 0 0 1 5.75 2h.5a.75.75 0 0 1 .75.75V21a.75.75 0 0 1-.75.75h-.5A.75.75 0 0 1 5 21V2.75ZM8.5 3.5h9.05c.83 0 1.28.97.74 1.6L16.1 8l2.19 2.9c.54.63.09 1.6-.74 1.6H8.5v-9Z" />
  </Svg>
);

/** Foco · un disco con un hueco: el temporizador. */
export const IconFoco = (p: Props): ReactElement => (
  <Svg {...p}>
    <path
      fillRule="evenodd"
      d="M12 3a9 9 0 1 0 9 9h-2a7 7 0 1 1-7-7V3Zm1 1.06V11h6.94A8.01 8.01 0 0 0 13 4.06Z"
    />
  </Svg>
);

/** Progreso · tres peldaños que suben. */
export const IconProgreso = (p: Props): ReactElement => (
  <Svg {...p}>
    <path d="M3 17.5A1.5 1.5 0 0 1 4.5 16H8v4H4.5A1.5 1.5 0 0 1 3 18.5v-1ZM9.5 11h5v9h-5v-9ZM16 5.5A1.5 1.5 0 0 1 17.5 4h2A1.5 1.5 0 0 1 21 5.5V20h-5V5.5Z" />
  </Svg>
);

/** Perfil · un disco partido, guiño al símbolo sin ser el símbolo. */
export const IconPerfil = (p: Props): ReactElement => (
  <Svg {...p}>
    <path d="M12 3a9 9 0 0 1 6.36 15.36A9 9 0 0 1 3 12a9 9 0 0 1 9-9Zm0 3.4a2.6 2.6 0 1 0 0 5.2 2.6 2.6 0 0 0 0-5.2Zm0 7.2c-2.6 0-4.9 1.3-6 3.3A7 7 0 0 0 12 19a7 7 0 0 0 6-2.1c-1.1-2-3.4-3.3-6-3.3Z" />
  </Svg>
);

export const IconCheck = ({ size = 16, className }: Props): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M5 12.5 10 17.5 19 7" />
  </svg>
);

export const IconSkip = ({ size = 16, className }: Props): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M6 12h12" />
  </svg>
);

export const IconMas = ({ size = 20, className }: Props): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconAtras = ({ size = 20, className }: Props): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M14.5 5 7.5 12l7 7" />
  </svg>
);

/** Las cinco divisas de tramo: chevrones que suben. Un solo vocabulario. */
export function Divisa({ tramo, size = 20 }: { tramo: string; size?: number }): ReactElement {
  const paths: Record<string, ReactElement> = {
    paso: <path d="M4 15 12 8l8 7" />,
    salto: (
      <>
        <path d="M4 17 12 10l8 7" />
        <path d="M4 12 12 5l8 7" />
      </>
    ),
    vuelo: (
      <>
        <path d="M2 15 12 7l10 8" />
        <path d="M6 19h12" />
      </>
    ),
    auge: (
      <>
        <path d="M4 17 12 10l8 7" />
        <path d="M4 6h16" />
      </>
    ),
    cenit: (
      <>
        <path d="M4 18 12 11l8 7" />
        <circle cx="12" cy="5.5" r="2.6" />
        <path d="M3 21h18" />
      </>
    ),
  };

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[tramo] ?? paths['paso']}
    </svg>
  );
}

/* ---------------------------------------------------------------------------
   LOS SECUNDARIOS — los destinos que en móvil viven dentro de Perfil y en
   escritorio tienen su sitio propio en el riel.

   Dos reglas heredadas del antibrief del paso 1, y ninguna es decorativa:
     · Ajustes NO es un engranaje. Son dos deslizadores, que además es lo que
       hay dentro: tema, piel, transparencia, hora de inicio del día.
     · Calendario NO repite la hoja de calendario de Hoy. El calendario de esta
       app es un registro vertical de lo que pasó, no una rejilla de mes, así
       que su icono es una tira de marcas: barras de distinta longitud. La
       Semana gira esa idea: siete columnas de pie, como su tira de días.
   --------------------------------------------------------------------------- */

/** Diario · una hoja escrita, con la esquina doblada. */
export const IconDiario = (p: Props): ReactElement => (
  <Svg {...p}>
    <path d="M6 3h7.2L19 8.6V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm7.6 1.9v3.4h3.4l-3.4-3.4ZM7.5 11.5h9v1.8h-9v-1.8Zm0 3.6h6v1.8h-6v-1.8Z" />
  </Svg>
);

/** Calendario · el registro: días de distinta densidad, uno debajo de otro. */
export const IconCalendario = (p: Props): ReactElement => (
  <Svg {...p}>
    <g>
      <rect x="3" y="4.5" width="14" height="2.6" rx="1.3" />
      <rect x="3" y="9.2" width="18" height="2.6" rx="1.3" />
      <rect x="3" y="13.9" width="9" height="2.6" rx="1.3" />
      <rect x="3" y="18.6" width="13" height="2.6" rx="1.3" />
    </g>
  </Svg>
);

/** Semana · siete columnas de distinta altura, una por día. */
export const IconSemana = (p: Props): ReactElement => (
  <Svg {...p}>
    <g>
      <rect x="2.5" y="9" width="2.2" height="11" rx="1.1" />
      <rect x="5.6" y="6" width="2.2" height="14" rx="1.1" />
      <rect x="8.7" y="11" width="2.2" height="9" rx="1.1" />
      <rect x="11.8" y="4" width="2.2" height="16" rx="1.1" />
      <rect x="14.9" y="8" width="2.2" height="12" rx="1.1" />
      <rect x="18" y="13" width="2.2" height="7" rx="1.1" />
      <rect x="21.1" y="15" width="1.4" height="5" rx="0.7" />
    </g>
  </Svg>
);

/** Revisión · el ciclo que se cierra. Un anillo abierto con su punta. */
export const IconRevision = (p: Props): ReactElement => (
  <Svg {...p}>
    <path
      fillRule="evenodd"
      d="M12 4a8 8 0 1 1-7.6 10.5l2.3-.75A5.6 5.6 0 1 0 12 6.4v2.7L7.3 6.2 12 3.3V4Z"
    />
  </Svg>
);

/** Círculo · tres presencias. Ni caras ni siluetas: discos. */
export const IconCirculo = (p: Props): ReactElement => (
  <Svg {...p}>
    <g>
      <circle cx="12" cy="6.2" r="3.1" />
      <circle cx="5.6" cy="16.4" r="3.1" />
      <circle cx="18.4" cy="16.4" r="3.1" />
    </g>
  </Svg>
);

/** Copiloto · dos turnos de una conversación, uno encima del otro. */
export const IconCopiloto = (p: Props): ReactElement => (
  <Svg {...p}>
    <g>
      <rect x="3" y="4" width="13" height="8.6" rx="3" />
      <rect x="8" y="11.4" width="13" height="8.6" rx="3" />
    </g>
  </Svg>
);

/** Ajustes · dos deslizadores. No un engranaje (antibrief, paso 1). */
export const IconAjustes = (p: Props): ReactElement => (
  <Svg {...p}>
    <g>
      <rect x="3" y="7.4" width="18" height="2.2" rx="1.1" />
      <rect x="3" y="14.4" width="18" height="2.2" rx="1.1" />
      <circle cx="8.5" cy="8.5" r="3" />
      <circle cx="15.5" cy="15.5" r="3" />
    </g>
  </Svg>
);

/** Cerrar · la equis. Hasta ahora se escribía «Cerrar» con palabras en todas partes. */
export const IconCerrar = ({ size = 18, className }: Props): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

/** Desplegar · la punta de flecha del selector. */
export const IconDespliega = ({ size = 16, className }: Props): ReactElement => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M7 10l5 5 5-5" />
  </svg>
);

/** Opciones · tres puntos. El menú de una fila. */
export const IconOpciones = ({ size = 20, className }: Props): ReactElement => (
  <Svg size={size} {...(className ? { className } : {})}>
    <g>
      <circle cx="12" cy="5" r="1.9" />
      <circle cx="12" cy="12" r="1.9" />
      <circle cx="12" cy="19" r="1.9" />
    </g>
  </Svg>
);

/** Ver · un ojo abierto. */
export const IconOjo = (p: Props): ReactElement => (
  <Svg {...p}>
    <path d="M12 5c4.6 0 8.3 3.1 9.7 6.4a1.5 1.5 0 0 1 0 1.2C20.3 15.9 16.6 19 12 19s-8.3-3.1-9.7-6.4a1.5 1.5 0 0 1 0-1.2C3.7 8.1 7.4 5 12 5Zm0 3.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7Z" />
  </Svg>
);

/** Ocultar · el mismo ojo, cruzado. */
export const IconOjoCerrado = (p: Props): ReactElement => (
  <Svg {...p}>
    <path d="M3.7 2.3a1 1 0 0 0-1.4 1.4l3 3C4 7.9 2.9 9.5 2.3 11.4a1.5 1.5 0 0 0 0 1.2C3.7 15.9 7.4 19 12 19c1.8 0 3.5-.5 5-1.3l3.3 3.3a1 1 0 0 0 1.4-1.4l-18-18Zm8.3 13.2a3.5 3.5 0 0 1-3.4-4.3l4.6 4.6c-.4.1-.8.2-1.2.2ZM12 5c4.6 0 8.3 3.1 9.7 6.4a1.5 1.5 0 0 1 0 1.2c-.5 1.2-1.3 2.4-2.3 3.4l-3.9-3.9a3.5 3.5 0 0 0-4.3-4.3L8.6 5.5C9.7 5.2 10.8 5 12 5Z" />
  </Svg>
);
